import React, { useContext, useEffect, useRef, useState } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { Snackbar, Alert } from "@mui/material";
import "./Card.css";
import { FilterCard } from "../FilterCard/FilterCard";
import { AdvancedCard } from "../AdvancedCard/AdvancedCard";
import { useLocation } from "react-router-dom";
import {
  isUserSubscribed,
  getUserSearchTerms,
  clearFormState,
} from "../../Utils/functions";
import ScheduleJobConfirmation from "../ScheduleJobConfirmation/ScheduleJobConfirmation";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { FilterContext } from "../../Context/FilterContext";
import { PicoContext } from "../../Context/PicoContext";
import { useTypewriterPlaceholder } from "../../hooks/useTypewriterPlaceholder";
import { EXAMPLE_QUESTIONS } from "../../Utils/exampleQuestions";

export const Card = ({
  search,
  scheduleSearch,
  defaultText,
  setQuery = () => { },
  query,
}) => {
  const location = useLocation();
  const isSearchPage = location.pathname.startsWith("/search");
  const isHomePage = location.pathname === "/";
  const isLiteratureReviewToolsPage = location.pathname === "/screening";
  const { user } = useAuthenticator((context) => [context.user]);
  const [text, setText] = useState(defaultText || "");
  const [placeholderDismissed, setPlaceholderDismissed] = useState(false);
  const animatedPlaceholder = useTypewriterPlaceholder(EXAMPLE_QUESTIONS, {
    enabled: text === "" && !placeholderDismissed,
  });
  const [searchMode, setSearchMode] = useState("public");
  const [availableSearchModes, setAvailableSearchModes] = useState(["public"]);
  const [isModelopen, setIsModelOpen] = useState(false);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [
    showScheduleConfirmationModal,
    setShowScheduleConfirmationModal,
  ] = useState(false);
  const filterCardRef = useRef();
  const advancedCardRef = useRef();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const { setFilters } = useContext(FilterContext);
  const { setPico } = useContext(PicoContext);
  const hasRestored = useRef(false);
  useEffect(() => {
    isUserSubscribed()
      .then((data) => {
        if (data.isActive) {
          setAvailableSearchModes(["public", "private", "hybrid"]);
        } else {
          setAvailableSearchModes(["public"]);
        }
      })
      .catch((error) => {
        console.error("Error checking subscription status:", error);
      });
  }, []);

  useEffect(() => {
    const savedState = getUserSearchTerms();

    if (savedState && !hasRestored.current) {
      hasRestored.current = true;
      if (savedState.question) {
        setText(savedState.question);
      }
      if (savedState.searchMode) {
        setSearchMode(savedState.searchMode);
      }
      if (savedState.query) {
        setQuery(savedState.query);
      }
      if (savedState.filters) {
        setFilters(savedState.filters);
      }
      if (savedState.pico) {
        setPico(savedState.pico);
      }
      clearFormState();
    } else if (isHomePage && !savedState && !hasRestored.current) {
      // If on Home page and no saved state, reset filters to defaults
      hasRestored.current = true;
      if (!user) {
        setText("What is the evidence for ");
      }
      setFilters({
        selectedSex: "",
        fromYear: "",
        toYear: "",
        age: "",
        abstracts: "",
        studyDesigns: [],
      });
    }
  }, [user, isHomePage]);

  const closeFilterCard = (e) => {
    if (e.target === filterCardRef.current) {
      setIsModelOpen(false);
    }
  };

  const closeAdvancedCard = (e) => {
    if (e.target === advancedCardRef.current) {
      setIsAdvancedModalOpen(false);
    }
  };
  const handleClick = () => {
    const trimmedText = text.trim();
    setText(trimmedText);
    search(trimmedText, searchMode);
  };

  const handleScheduleSearch = (frequency) => {
    const trimmedText = text.trim();
    setText(trimmedText);
    scheduleSearch(trimmedText, searchMode, frequency);
    setShowScheduleConfirmationModal(false);
  };
  const toggleModal = () => {
    setIsModelOpen(!isModelopen);
  };

  const toggleModalAdvanced = () => {
    setIsAdvancedModalOpen(!isAdvancedModalOpen);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className="Card">
      {/* <div className="Header">
        <span className="GreenText">A</span>ccurate{" "}
        <span className="GreenText">I</span>nformation
      </div> */}
      <div className="SearchContainer">
        <div className="InputQuestionDiv InputQuestionDiv--relative">
          <textarea
            rows={5}
            cols={5}
            className="InputQuestion InputQuestion--withDropdown"
            placeholder={animatedPlaceholder}
            value={text}
            onFocus={() => setPlaceholderDismissed(true)}
            onClick={() => setPlaceholderDismissed(true)}
            onChange={(e) => {
              setText(e.target.value);
            }}
          />
        </div>
      </div>
      {isSearchPage && (
        <div>
          <div className="CardButtons">
            <div className="Button FilterButton" onClick={toggleModal}>
              Filter
            </div>
            <div className="Button FilterButton" onClick={toggleModalAdvanced}>
              Advanced
            </div>
          </div>

          <div classNames="searchButtons">
            <div
              className="Button SubmitButton SearchButton"
              onClick={handleClick}
            >
              Submit your question
            </div>
            <div
              className="Button SubmitButton SearchButton"
              onClick={() => setShowScheduleConfirmationModal(true)}
            >
              Schedule Periodic Runs
            </div>
          </div>
        </div>
      )}
      {!isSearchPage && (
        <div>
          <div className="CardButtons">
            {!isLiteratureReviewToolsPage && (
              <div className="Button FilterButton" onClick={toggleModal}>
                Filter
              </div>
            )}
            <div className="Button SubmitButton" onClick={handleClick}>
              Submit your question
            </div>
          </div>
        </div>
      )}
      {isModelopen && (
        <div
          className="FilterCardOverlay"
          ref={filterCardRef}
          onClick={closeFilterCard}
        >
          <FilterCard
            toggleModal={toggleModal}
            setSearchMode={setSearchMode}
            searchMode={searchMode}
            availableSearchModes={availableSearchModes}
          />
        </div>
      )}
      {isAdvancedModalOpen && (
        <div
          className="FilterCardOverlay"
          ref={advancedCardRef}
          onClick={closeAdvancedCard}
        >
          <AdvancedCard
            toggleModalAdvanced={toggleModalAdvanced}
            setQuery={setQuery}
            query={query}
          />
        </div>
      )}
      {showScheduleConfirmationModal && (
        <ScheduleJobConfirmation
          isVisible={showScheduleConfirmationModal}
          onClose={() => setShowScheduleConfirmationModal(false)}
          onConfirm={handleScheduleSearch}
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};
